/**
 * A QR module bitmap as one SVG path.
 *
 * Split out of `ui/QrCode` so it can be tested: the geometry is the half of a QR
 * code that can be wrong in a way nobody sees. A code with a dropped module still
 * renders as a convincing square, and the panel's own tests cannot scan it — so the
 * mapping from bitmap to path is pinned here, and the component is left with
 * nothing but the element it wraps around this string.
 */

/**
 * Merges each horizontal run of dark modules into a single rectangle rather than
 * emitting one per module. A station URL is a 29-module symbol with several hundred
 * dark cells; runs bring that to a few dozen commands, which is a DOM the browser
 * prints without thinking about it.
 *
 * `M{x} {y}h{n}v1h-{n}z` — absolute move, then relative, so the numbers stay small
 * and the string stays legible in a DOM inspector when a code does not scan and
 * somebody has to work out why.
 *
 * The path is in *module* units: the caller supplies the `viewBox`, and nothing here
 * knows how large the code will be drawn.
 */
export function qrPath(bitmap: readonly (readonly boolean[])[]): string {
  const commands: string[] = [];

  bitmap.forEach((row, y) => {
    let runStart: number | undefined;

    /**
     * One past the end. A run that reaches the right edge is then closed by the same
     * branch as every other run, rather than by a duplicate of it after the loop —
     * which is where an off-by-one in this function would live.
     */
    for (let x = 0; x <= row.length; x += 1) {
      const isDark = row[x] === true;

      if (isDark && runStart === undefined) {
        runStart = x;
        continue;
      }

      if (!isDark && runStart !== undefined) {
        const width = x - runStart;
        commands.push(`M${runStart} ${y}h${width}v1h-${width}z`);
        runStart = undefined;
      }
    }
  });

  return commands.join("");
}
